import { UserRole } from 'src/types/user'
import { Project, ProjectsPageContent, ProjectViewMode } from '../../types/projects'
import { SubscriptionPlanType } from '../../types/subscription'
import { ROUTES } from '../../types/routes'

export const CONSTANT_A = 'CONSTANT_A'

export const DEFAULT_TAKE = 10

export const SEARCH_DEBOUNCE_MS = 400

export const MIN_SEARCH_LENGTH = 3

export const roles = [
  {
    label: 'Admin',
    value: UserRole.ADMIN,
  },
  {
    label: 'Contributor',
    value: UserRole.CONTRIBUTOR,
  },
  {
    label: 'Viewer',
    value: UserRole.VIEWER,
  },
]

export const plans = [
  {
    id: 'Free',
    label: 'Free',
    value: SubscriptionPlanType.FREE,
    disabled: false,
    maxMembers: 1,
  },
  {
    id: 'Pro',
    label: 'Pro',
    value: SubscriptionPlanType.PRO,
    disabled: true, // Don't allow setting Pro plan for Platform Admins
    maxMembers: 10,
  },
  {
    id: 'Enterprise',
    label: 'Enterprise',
    value: SubscriptionPlanType.ENTERPRISE,
    disabled: false,
    maxMembers: null, // no limit for enterprise
  },
]

export const userTypes = [
  {
    id: 'CarbonDeveloper',
    label: 'Carbon developer',
    value: 'CarbonDeveloper',
  },
  {
    id: 'LandOwner',
    label: 'Land owner or steward',
    value: 'LandOwner',
  },
  {
    id: 'Investor',
    label: 'Investor',
    value: 'Investor',
  },
  {
    id: 'Government',
    label: 'Government Representative',
    value: 'Government',
  },
  {
    id: 'Curious',
    label: 'Curious about carbon',
    value: 'Curious',
  },
]

export const userPurposes = [
  {
    id: 'DevelopProject',
    label: 'Looking to develop a carbon project',
    value: 'DevelopProject',
  },
  {
    id: 'FindSolutions',
    label: 'Searching for carbon solutions to my land',
    value: 'FindSolutions',
  },
  {
    id: 'UnderstandMarket',
    label: 'Understanding the market & landscape',
    value: 'UnderstandMarket',
  },
  {
    id: 'Invest',
    label: 'Want to invest in carbon credits',
    value: 'Invest',
  },
]

export const stylesDrawPolygon = [
  // ACTIVE (being drawn)
  // line stroke
  {
    id: 'gl-draw-line',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#FFA800',
      'line-dasharray': [0.2, 2],
      'line-width': 3,
    },
  },
  // polygon fill
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': '#6CC0A7',
      'fill-outline-color': '#FFA800',
      'fill-opacity': 0.32,
    },
  },
  // polygon mid points
  {
    id: 'gl-draw-polygon-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 3,
      'circle-color': '#fbb03b',
    },
  },
  // polygon outline stroke
  // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#FFA800',
      'line-width': 3,
    },
  },
  // vertex point halos
  {
    id: 'gl-draw-polygon-and-line-vertex-halo-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#FFF',
    },
  },
  // vertex points
  {
    id: 'gl-draw-polygon-and-line-vertex-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 6,
      'circle-color': '#000000',
      'circle-stroke-width': 4,
      'circle-stroke-color': '#FFA800',
    },
  },
] as object[]

export const ACTIVE_COUNTRIES = ['GHA', 'AGO', 'MOZ', 'COD', 'ZMB', 'PRT']

export const CHUNK_SIZE = 5 * 1024 * 1024

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

export const WIDGET_TYPES = [
  // { id: 'statistic', label: 'Statistic', value: 'statistic' },
  // { id: 'bar-height', label: 'Bar Height', value: 'bar-height' },
  { id: 'histogram', label: 'Histogram', value: 'histogram' },
  // { id: 'filter', label: 'Filter', value: 'filter' },
]

export const PROJECTS_PAGE_CONTENT: Record<ProjectViewMode, ProjectsPageContent> = {
  tenant: {
    title: 'Welcome to your projects',
    subtitle: 'Select a project to start ...',
    emptyTitle: "You don't have any projects created yet.",
    emptySubtitle: 'Create your first project, and it will immediately appear in this tab.',
  },
  public: {
    title: 'Landscapes',
    subtitle: 'Explore landscape-level insights ...',
    emptyTitle: 'No public projects available.',
    emptySubtitle: 'Public projects will appear here when available.',
  },
}

export const GET_PROJECT_URL = (project: Project) =>
  project.isPublic
    ? ROUTES.LANDSCAPES_PROJECT_DETAILS.replace(':id', project.id)
    : ROUTES.PROJECT_DETAILS.replace(':id', project.id)

export const ONBOARDING_VIDEO_URL = 'https://ramodevstorage.blob.core.windows.net/onboarding-media/onboarding-video.mp4'

export const CREATE_PROJECT_STAGE_DESCRIPTIONS: Record<string, string> = {
  Exploring: 'Initial project design',
  Implementation: 'Planning and development',
  Monitoring: 'Ongoing tracking',
}

export const STAGE_ORDER = Object.keys(CREATE_PROJECT_STAGE_DESCRIPTIONS)

export const DATA_ROOM_FOLDER_NAME_MAX_LENGTH = 50
export const DATA_ROOM_FOLDER_MAX_DEPTH = 10

export const DATA_ROOM_ALLOWED_EXTENSIONS = ['pdf', 'jpeg', 'jpg', 'png', 'bmp', 'tiff', 'docx', 'xlsx', 'pptx', 'html']

export const DATA_ROOM_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/tiff',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/html',
  'application/octet-stream', // fallback for files not properly recognized
]

export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024

/** Max number of projects deletable in one bulk request. Must stay in sync with the backend `ArrayMaxSize`. */
export const MAX_PROJECT_BULK_DELETE = 100
